<template>
  <div class="relative">
    <div
      class="flex gap-1 mt-4 items-center cursor-pointer"
      @click="open = true"
    >
      <img
        src="/icons/caution.png"
        width="20px"
      >
      <p class="text-[#A3A3A3] text-xs">
        Spot a mistake?
      </p>
    </div>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open"
          class="fixed inset-0 z-[100]"
        >
          <div
            class="absolute inset-0 bg-black/40"
            @click="close"
          />
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="translate-y-full"
            enter-to-class="translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0"
            leave-to-class="translate-y-full"
          >
            <div
              v-if="open"
              class="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl overflow-hidden"
            >
              <div class="px-5 pt-4 pb-3 border-b">
                <div class="mx-auto h-1 w-10 rounded-full bg-gray-300 mb-3" />
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-base font-semibold text-gray-900">
                    Spot a Mistake?
                  </h3>
                  <button
                    class="flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                    @click="close"
                  >
                    <span class="text-xl -mt-px">&times;</span>
                  </button>
                </div>
                <p class="text-sm text-gray-600 mt-1">
                  Help us keep our questions accurate and clear. Please select
                  what seems wrong:
                </p>
              </div>

              <div class="max-h-[60vh] overflow-y-auto p-5">
                <div class="space-y-4">
                  <div
                    v-for="reason in reasons"
                    :key="reason"
                    class="flex items-center"
                  >
                    <input
                      :id="`reason-${reason}`"
                      v-model="selectedReason"
                      type="radio"
                      :value="reason"
                      class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    >
                    <label
                      :for="`reason-${reason}`"
                      class="ml-3 block text-sm text-gray-900"
                    >
                      {{ reason }}
                    </label>
                  </div>

                  <div
                    v-if="selectedReason === 'Other'"
                    class="mt-4"
                  >
                    <textarea
                      v-model="otherReason"
                      rows="4"
                      placeholder="enter your reason"
                      class="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div class="p-4 border-t bg-gray-50">
                <button
                  class="w-full bg-blue-600 text-white py-3 rounded-full font-semibold hover:bg-blue-700 transition"
                  @click="submit"
                >
                  Submit
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useSnackbarStore } from "@/stores/snackbarStore";

const emit = defineEmits(["open", "close", "submit"]);

const open = ref(false);
const selectedReason = ref("");
const otherReason = ref("");
const snackbar = useSnackbarStore();

const reasons = [
  "Question Details",
  "Image or Graphic",
  "Answer Options",
  "Incorrect Answer",
  "Explanation Content",
  "Explanation Image",
  "Other",
];

watch(open, (v) => {
  if (v) {
    emit("open");
  } else {
    emit("close");
    selectedReason.value = "";
    otherReason.value = "";
  }
});

const close = () => {
  open.value = false;
};

const submit = () => {
  if (!selectedReason.value) {
    alert("Please select a reason.");
    return;
  }
  if (selectedReason.value === 'Other' && !otherReason.value.trim()) {
    alert("Please enter your reason for 'Other'.");
    return;
  }

  const report = {
    reason: selectedReason.value,
    details: otherReason.value,
  };

  emit("submit", report);
  snackbar.trigger(
      `Mistake Report Submitted: ${report.reason + " " + report?.details}`,
      "success"
    );

  close();
};
</script>